import { ensureArray } from '../../utils/array-utils';
import {
  VisibleFilters,
  defaultVisibleFilters,
} from '../../components/common/visible-filters';
import {
  VisibleProperties,
  // defaultVisibleProperties,
} from '../../components/common/visible-properties';
import { GetJobStatusListResult } from '../../data/useGetJobStatusList';
import {
  getJobStatus,
  getTaskStatus,
  getTaskStatusNumber,
} from '../../globals';
import { QuerySearchJobsArgs, QuerySearchTasksArgs } from '../../types/mapper';
import { JobStatusEnumValueType } from '../job/types/job-status-enum';

export type JobsFilter = Required<Omit<QuerySearchJobsArgs, 'statuses'>>;
export type TasksFilter = Required<QuerySearchTasksArgs>;
export type JobTasksFilter = Omit<TasksFilter, 'jobs'>;

export const emptyJobsFilter: JobsFilter = {
  clients: [],
  myWork: false,
  staffIds: [],
  managerIds: [],
  partnerIds: [],
  assigneeIds: [],
  statusIds: [],
  text: '',
  familyGroupIds: [],
  templateIds: [],
  dueDateBegin: null,
  dueDateEnd: null,
  startDateBegin: null,
  startDateEnd: null,
  closedDateBegin: null,
  closedDateEnd: null,
  completedDateBegin: null,
  completedDateEnd: null,
  dueDateOverdue: false,
  take: null,
};

export const emptyTasksFilter: TasksFilter = {
  clients: [],
  entities: [],
  jobs: [],
  myWork: false,
  staff: [],
  statuses: [],
  text: '',
  familyGroupIds: [],
  dueDateBegin: null,
  dueDateEnd: null,
  completedDateBegin: null,
  completedDateEnd: null,
  dueDateOverdue: false,
  take: null,
  // this is for the main task list, we never want jobs for closed tasks
  // and it won't ever be on the filter bar, so we have to default it here
  excludeClosedJobTasks: true,
};

export const emptyJobTasksFilter: JobTasksFilter = {
  clients: [],
  entities: [],
  myWork: false,
  staff: [],
  statuses: [],
  text: '',
  familyGroupIds: [],
  dueDateBegin: null,
  dueDateEnd: null,
  completedDateBegin: null,
  completedDateEnd: null,
  dueDateOverdue: false,
  take: null,
  // this one will never be set to false, this is the one used for a specific job
  excludeClosedJobTasks: false,
};

export const emptyCompletedJobsFilter = (
  completedDateBegin: string,
  completedDateEnd: string
): JobsFilter => ({
  clients: [],
  myWork: false,
  staffIds: [],
  managerIds: [],
  partnerIds: [],
  assigneeIds: [],
  // statuses: [],
  statusIds: [],
  text: '',
  familyGroupIds: [],
  templateIds: [],
  dueDateBegin: null,
  dueDateEnd: null,
  startDateBegin: null,
  startDateEnd: null,
  closedDateBegin: null,
  closedDateEnd: null,
  completedDateBegin: completedDateBegin,
  completedDateEnd: completedDateEnd,
  dueDateOverdue: false,
  take: null,
});

const toggleablePropertiesKey: {
  taskList: Record<keyof VisibleProperties['taskList'], string>;
  taskBoard: Record<keyof VisibleProperties['taskBoard'], string>;
  jobList: Record<keyof VisibleProperties['jobList'], string>;
  jobBoard: Record<keyof VisibleProperties['jobBoard'], string>;
  completedJobTable: Record<
    keyof VisibleProperties['completedJobTable'],
    string
  >;
} = {
  taskList: {
    debtor: 'tasklistdebtor',
    job: 'tasklistjob',
    task: 'tasklisttask',
    client: 'tasklistclient',
    assigned: 'tasklistassigned',
    status: 'taskliststatus',
    dueDate: 'tasklistduedate',
    estimatedTime: 'tasklistestimatedtime',
  },
  taskBoard: {
    debtor: 'taskboarddebtor',
    job: 'taskboardjob',
    task: 'taskboardtask',
    client: 'taskboardclient',
    assigned: 'taskboardassigned',
    dueDate: 'taskboardduedate',
    estimatedTime: 'taskboardestimatedtime',
  },
  jobList: {
    debtor: 'joblistdebtor',
    job: 'joblistjob',
    assignee: 'joblistassignee',
    partner: 'joblistpartner',
    manager: 'joblistmanager',
    staff: 'jobliststaff',
    status: 'jobliststatus',
    dueDate: 'joblistduedate',
    budget: 'joblistbudget',
    tasks: 'joblisttasks',
  },
  jobBoard: {
    debtor: 'jobboarddebtor',
    job: 'jobboardjob',
    assignee: 'jobboardassignee',
    partner: 'jobboardpartner',
    manager: 'jobboardmanager',
    staff: 'jobboardstaff',
    dueDate: 'jobboardduedate',
    tasks: 'jobboardtasks',
  },
  completedJobTable: {
    assignee: 'completedjobtableassignee',
    partner: 'completedjobtablepartner',
    manager: 'completedjobtablemanager',
    staff: 'completedjobtablestaff',
    job: 'completedjobtablejob',
    debtor: 'completedjobtabledebtor',
    startDate: 'completedjobtablestartDate',
    dueDate: 'completedjobtabledueDate',
    completedDate: 'completedjobtablecompletedDate',
    turnaround: 'completedjobtableturnaround',
    delivery: 'completedjobtabledelivery',
  },
};

export const parseJobsFilter = (
  json: any,
  jobStatuses: GetJobStatusListResult[]
): JobsFilter => {
  const parsed: Partial<JobsFilter> = {
    clients: json.selectedClients?.map(({ value }: any) => value),
    myWork: json.myWorkOn,
    assigneeIds: json.selectedAssignee?.map(({ value }: any) => value),
    staffIds: json.selectedStaff?.map(({ value }: any) => value),
    managerIds: json.selectedManager?.map(({ value }: any) => value),
    partnerIds: json.selectedPartner?.map(({ value }: any) => value),
    statusIds: json.selectedJobStatusIds?.map(({ value }: any) => value),
    text: json.searchText,
    familyGroupIds: json.selectedFamilyGroups?.map(({ value }: any) => value),
    dueDateBegin: json.dueDateBegin || null,
    dueDateEnd: json.dueDateEnd || null,
    startDateBegin: json.startDateBegin || null,
    startDateEnd: json.startDateEnd || null,
    closedDateBegin: json.closedDateBegin || null,
    closedDateEnd: json.closedDateEnd || null,
    completedDateBegin: json.completedDateBegin || null,
    completedDateEnd: json.completedDateEnd || null,
    dueDateOverdue: json.dueDateOverdue,
  };

  // check if they had the old selectedJobStatuses property, which had the numeric value of the job status enum
  const oldStatuses: JobStatusEnumValueType[] | undefined =
    json.selectedJobStatuses?.map(({ value }: { value: number }) =>
      getJobStatus(value)
    );

  // if they had some old statuses,
  // and don't have any new statuses, turn the old statuses into new statuses
  // the "don't have new" is important, new trumps old. We should not get a mix unless someone gets the value in the db mangled
  // but still, this is Correct behaviour to check
  if (
    (!parsed.statusIds || parsed.statusIds.length === 0) &&
    oldStatuses &&
    oldStatuses.length > 0
  ) {
    // we have old enums. Look up the corresponding new object
    parsed.statusIds = oldStatuses
      .map((statusEnum) =>
        jobStatuses.find((status) => status.status === statusEnum)
      )
      .map((status) => status?.cloudId)
      .filter((id): id is string => typeof id === 'string');
  }

  const result = {
    ...emptyJobsFilter,
    ...Object.fromEntries(
      Object.entries(parsed).filter(([, v]) => v !== undefined)
    ),
  };
  return result;
};

export const serialiseJobsFilter = (filter: JobsFilter) => {
  return {
    selectedClients: ensureArray(filter.clients).map((value: string) => ({
      value,
    })),
    myWorkOn: filter.myWork,
    selectedAssignee: ensureArray(filter.assigneeIds).map((value: string) => ({
      value,
    })),
    selectedStaff: ensureArray(filter.staffIds).map((value: string) => ({
      value,
    })),
    selectedManager: ensureArray(filter.managerIds).map((value: string) => ({
      value,
    })),
    selectedPartner: ensureArray(filter.partnerIds).map((value: string) => ({
      value,
    })),
    selectedJobStatusIds: ensureArray(filter.statusIds).map(
      (value: string) => ({ value })
    ),
    searchText: filter.text,
    selectedFamilyGroups: ensureArray(filter.familyGroupIds).map(
      (value: string) => ({ value })
    ),
    dueDateBegin: filter.dueDateBegin,
    dueDateEnd: filter.dueDateEnd,
    startDateBegin: filter.startDateBegin,
    startDateEnd: filter.startDateEnd,
    closedDateBegin: filter.closedDateBegin,
    closedDateEnd: filter.closedDateEnd,
    completedDateBegin: filter.completedDateBegin,
    completedDateEnd: filter.completedDateEnd,
    dueDateOverdue: filter.dueDateOverdue,
  };
};

export const parseTasksFilter = (json: any): TasksFilter => {
  const parsed: Partial<TasksFilter> = {
    clients: json.selectedClients?.map(({ value }: any) => value),
    entities: json.selectedEntities?.map(({ value }: any) => value),
    jobs: json.selectedJobs?.map(({ value }: any) => value),
    myWork: json.myWorkOn,
    staff: json.selectedStaff?.map(({ value }: any) => value),
    statuses: json.selectedTaskStatuses?.map(({ value }: any) =>
      getTaskStatus(value)
    ),
    // startDate: undefined,
    // endDate: undefined,
    text: json.searchText,
    familyGroupIds: json.selectedFamilyGroups?.map(({ value }: any) => value),
    dueDateBegin: json.dueDateBegin || null,
    dueDateEnd: json.dueDateEnd || null,
    completedDateBegin: json.completedDateBegin || null,
    completedDateEnd: json.completedDateEnd || null,
    dueDateOverdue: json.dueDateOverdue,
  };
  const result = {
    ...emptyTasksFilter,
    ...Object.fromEntries(
      Object.entries(parsed).filter(([, v]) => v !== undefined)
    ),
  };
  return result;
};

export const serialiseTasksFilter = (filter: TasksFilter) => {
  return {
    selectedClients: ensureArray(filter.clients).map((value: string) => ({
      value,
    })),
    selectedEntities: ensureArray(filter.entities).map((value: string) => ({
      value,
    })),
    selectedJobs: ensureArray(filter.jobs).map((value: string) => ({
      value,
    })),
    myWorkOn: filter.myWork,
    selectedStaff: ensureArray(filter.staff).map((value: string) => ({
      value,
    })),
    selectedTaskStatuses: filter.statuses?.map((value) => ({
      value: getTaskStatusNumber(value),
    })),
    searchText: filter.text,
    selectedFamilyGroups: ensureArray(filter.familyGroupIds).map(
      (value: string) => ({ value })
    ),
    dueDateBegin: filter.dueDateBegin,
    dueDateEnd: filter.dueDateEnd,
    completedDateBegin: filter.completedDateBegin,
    completedDateEnd: filter.completedDateEnd,
    dueDateOverdue: filter.dueDateOverdue,
  };
};

export const parseVisibleFilters = (json: any): VisibleFilters => {
  const filterableProperties =
    json.filterableProperties &&
    Object.fromEntries(
      json.filterableProperties.map(({ key, selected }: any) => [key, selected])
    );
  const parsed: VisibleFilters = {
    allFamilyGroup: filterableProperties?.['all_family_group'],
    allCompletedDate: filterableProperties?.['all_completed_date'],
    jobStartDate: filterableProperties?.['job_start_date'],
    jobAssignee: filterableProperties?.['job_assignee'],
  };
  return {
    ...defaultVisibleFilters,
    ...Object.fromEntries(
      Object.entries(parsed).filter(([, v]) => v !== undefined)
    ),
  };
};

export const serialiseVisibleFilters = (visibleFilters: VisibleFilters) => {
  return [
    { key: 'all_family_group', selected: visibleFilters.allFamilyGroup },
    { key: 'all_completed_date', selected: visibleFilters.allCompletedDate },
    { key: 'job_start_date', selected: visibleFilters.jobStartDate },
    { key: 'job_assignee', selected: visibleFilters.jobAssignee },
  ];
};

// TODO it seems like these don't take into account the default value for properties that were added after the value
// was serialised to a string. This'd be bad?
export const parseJobBoardVisibleProperties = (
  json: any
): VisibleProperties['jobBoard'] => {
  const Key = Object.fromEntries(
    Object.entries(toggleablePropertiesKey.jobBoard).map(([k, v]) => [v, k])
  );
  return (
    json.toggleableProperties &&
    Object.fromEntries(
      json.toggleableProperties.map(({ key, selected }: any) => [
        Key[key],
        selected,
      ])
    )
  );
};

export const serialiseJobBoardVisibleProperties = (
  visibleProperties: VisibleProperties
) =>
  Object.entries(visibleProperties.jobBoard).map(([k, selected]) => ({
    key: toggleablePropertiesKey.jobBoard[
      k as keyof VisibleProperties['jobBoard']
    ],
    selected,
  }));

export const parseJobListVisibleProperties = (
  json: any
): VisibleProperties['jobList'] => {
  const Key = Object.fromEntries(
    Object.entries(toggleablePropertiesKey.jobList).map(([k, v]) => [v, k])
  );
  return (
    json.toggleableProperties &&
    Object.fromEntries(
      json.toggleableProperties.map(({ key, selected }: any) => [
        Key[key],
        selected,
      ])
    )
  );
};

export const serialiseJobListVisibleProperties = (
  visibleProperties: VisibleProperties
) =>
  Object.entries(visibleProperties.jobList).map(([k, selected]) => ({
    key: toggleablePropertiesKey.jobList[
      k as keyof VisibleProperties['jobList']
    ],
    selected,
  }));

export const parseTaskBoardVisibleProperties = (
  json: any
): VisibleProperties['taskBoard'] => {
  const Key = Object.fromEntries(
    Object.entries(toggleablePropertiesKey.taskBoard).map(([k, v]) => [v, k])
  );
  return (
    json.toggleableProperties &&
    Object.fromEntries(
      json.toggleableProperties.map(({ key, selected }: any) => [
        Key[key],
        selected,
      ])
    )
  );
};

export const serialiseTaskBoardVisibleProperties = (
  visibleProperties: VisibleProperties
) =>
  Object.entries(visibleProperties.taskBoard).map(([k, selected]) => ({
    key: toggleablePropertiesKey.taskBoard[
      k as keyof VisibleProperties['taskBoard']
    ],
    selected,
  }));

export const parseTaskListVisibleProperties = (
  json: any
): VisibleProperties['taskList'] => {
  const Key = Object.fromEntries(
    Object.entries(toggleablePropertiesKey.taskList).map(([k, v]) => [v, k])
  );
  return (
    json.toggleableProperties &&
    Object.fromEntries(
      json.toggleableProperties.map(({ key, selected }: any) => [
        Key[key],
        selected,
      ])
    )
  );
};

export const serialiseTaskListVisibleProperties = (
  visibleProperties: VisibleProperties
) =>
  Object.entries(visibleProperties.taskList).map(([k, selected]) => ({
    key: toggleablePropertiesKey.taskList[
      k as keyof VisibleProperties['taskList']
    ],
    selected,
  }));

export const parseCompletedJobTableVisibleProperties = (
  json: any
): VisibleProperties['completedJobTable'] => {
  const Key = Object.fromEntries(
    Object.entries(toggleablePropertiesKey.completedJobTable).map(([k, v]) => [
      v,
      k,
    ])
  );
  return (
    json.toggleableProperties &&
    Object.fromEntries(
      json.toggleableProperties.map(({ key, selected }: any) => [
        Key[key],
        selected,
      ])
    )
  );
};
export const serialiseCompletedJobTableVisibleProperties = (
  visibleProperties: VisibleProperties
) =>
  Object.entries(visibleProperties.completedJobTable).map(([k, selected]) => ({
    key: toggleablePropertiesKey.completedJobTable[
      k as keyof VisibleProperties['completedJobTable']
    ],
    selected,
  }));

export const equals = (a: any, b: any): boolean =>
  a === b ||
  (Array.isArray(a) &&
    Array.isArray(b) &&
    a.length === b.length &&
    (() => {
      const sortedB = [...b].sort();
      return [...a].sort().every((a, i) => equals(a, sortedB[i]));
    })()) ||
  (!Array.isArray(a) &&
    !Array.isArray(b) &&
    typeof a === 'object' &&
    typeof b === 'object' &&
    a !== undefined &&
    a !== null &&
    b !== undefined &&
    b !== null &&
    [...new Set([...Object.keys(a), ...Object.keys(b)])].every((k) =>
      equals(a[k], b[k])
    ));

export const getActiveJobsFilter = (
  filter: JobsFilter,
  show: VisibleFilters
): JobsFilter => {
  const {
    myWork,
    partnerIds,
    managerIds,
    staffIds,
    assigneeIds,
    dueDateOverdue,
    dueDateBegin,
    dueDateEnd,
    familyGroupIds,
    completedDateBegin,
    completedDateEnd,
    startDateBegin,
    startDateEnd,
    ...rest
  } = filter;
  const result = {
    ...emptyJobsFilter,
    ...rest,
    myWork,
    ...(!myWork && { partnerIds, managerIds, staffIds, assigneeIds }),
    ...(dueDateOverdue ? { dueDateOverdue } : { dueDateBegin, dueDateEnd }),
    ...(show.allFamilyGroup && { familyGroupIds }),
    ...(show.allCompletedDate && { completedDateBegin, completedDateEnd }),
    ...(show.jobStartDate && { startDateBegin, startDateEnd }),
  };
  return result;
};

export const getActiveTasksFilter = (
  filter: TasksFilter,
  show: VisibleFilters
): TasksFilter => {
  const {
    myWork,
    staff,
    dueDateOverdue,
    dueDateBegin,
    dueDateEnd,
    familyGroupIds,
    completedDateBegin,
    completedDateEnd,
    ...rest
  } = filter;
  const result = {
    ...emptyTasksFilter,
    ...rest,
    myWork,
    ...(!myWork && { staff }),
    ...(dueDateOverdue ? { dueDateOverdue } : { dueDateBegin, dueDateEnd }),
    ...(show.allFamilyGroup && { familyGroupIds }),
    ...(show.allCompletedDate && { completedDateBegin, completedDateEnd }),
  };
  return result;
};

export const getActiveJobTasksFilter = (
  filter: JobTasksFilter,
  show: VisibleFilters
): JobTasksFilter => {
  const {
    myWork,
    staff,
    dueDateOverdue,
    dueDateBegin,
    dueDateEnd,
    familyGroupIds,
    completedDateBegin,
    completedDateEnd,
    ...rest
  } = filter;
  return {
    ...emptyJobTasksFilter,
    ...rest,
    myWork,
    ...(!myWork && { staff }),
    ...(dueDateOverdue ? { dueDateOverdue } : { dueDateBegin, dueDateEnd }),
    ...(show.allFamilyGroup && { familyGroupIds }),
    ...(show.allCompletedDate && { completedDateBegin, completedDateEnd }),
  };
};
